import React, { useEffect, useRef } from 'react'
import { View } from 'react-native'
import { useRoute, RouteProp } from '@react-navigation/core'
import { useDispatch, useSelector } from 'react-redux'
import { AppStackParamList } from '@src/navigation/types'
import { Analytics, CustomEventTypes, buildTutorialMixpanelEventProperties } from '@src/config'
import { useSnack } from '@src/utils/navigatorContext'
import { LoadingIndicator } from '@components'
import { NavigationContainer } from '@src/screens/Common/containers'
import { StyleService, useStyleSheet } from '@src/style/service'
import { ListTutorial } from '../components/ListTutorial'
import { TutorialTypes } from '../models/tutorials.types'
import { CarouselTutorial } from '../components/CarouselTutorial'
import { currentTutorialSelector } from '../models/tutorials.selectors'

const LOADING_ERROR = 'Failed to load tutorial, please check your internet connection.'

export const CMSTutorial = () => {
  const styles = useStyleSheet(themedStyles)
  const {
    params: { tutorialId },
  } = useRoute<RouteProp<AppStackParamList, 'CMSTutorial'>>()
  const dispatch = useDispatch()
  const showSnack = useSnack()
  const tutorial = useSelector(currentTutorialSelector)
  const trackedTutorialIdRef = useRef<string | null>(null)
  const isLoading = !tutorial.id || tutorial.id !== tutorialId

  useEffect(() => {
    dispatch({
      type: 'tutorials/fetchTutorial',
      payload: { id: tutorialId },
      failure: () => {
        showSnack(LOADING_ERROR, null, 'error')
      },
    })
  }, [dispatch, showSnack, tutorialId])

  useEffect(() => {
    if (isLoading || trackedTutorialIdRef.current === tutorial.id) {
      return
    }

    trackedTutorialIdRef.current = tutorial.id

    Analytics.track(
      CustomEventTypes.TutorialStarted,
      buildTutorialMixpanelEventProperties({
        tutorialId: tutorial.id,
        tutorialName: tutorial.name,
        sensorKind: tutorial.sensorKind,
        platform: tutorial.platform ?? undefined,
      }),
    )
  }, [isLoading, tutorial.id, tutorial.name, tutorial.platform, tutorial.sensorKind])

  if (isLoading) {
    return (
      <NavigationContainer title="Loading...">
        <View style={styles.loadingContainer}>
          <LoadingIndicator size="large" />
        </View>
      </NavigationContainer>
    )
  }

  switch (tutorial.tutorialType) {
    case TutorialTypes.Carousel:
      return <CarouselTutorial tutorial={tutorial} key={tutorial.id} />
    case TutorialTypes.List:
      return <ListTutorial tutorial={tutorial} />
    default:
      return null
  }
}

const themedStyles = StyleService.create({
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
})
